import ReactEChartsCore from 'echarts-for-react/lib/core';
import { EChartsOption, EChartsReactProps } from 'echarts-for-react/lib/types';
import { BarChart, LineChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
  ComponentRef,
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectChartOptions } from 'store/selectors/DisplayOptions/displayOptionsSelectors';
import {
  applyChartThemeToOption,
  getChartContainerStyles,
  normalizeChartOptions,
} from 'utils/charts/chartStyle.utils';
import { resetChartZoom } from './utils/chartZoom.utils';

// Tell ECharts which components to use
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
]);

export type BaseChartProps = {
  option?: EChartsOption | null;
  onEvents?: EChartsReactProps['onEvents'];
  eChartsRenderer?: 'canvas' | 'svg';
  notMerge?: boolean;
  autoResize?: boolean;
  lazyUpdate?: boolean;
  customStyles?: CSSProperties;
  height?: string | number;
  width?: string | number;
  controlsLeft?: ReactNode;
  controlsRight?: ReactNode;
  showResetZoom?: boolean;
  emptyMessage?: ReactNode;
  onChartReady?: EChartsReactProps['onChartReady'];
} & {};

/**
 * Render a themed ECharts instance with optional control overlays.
 *
 * @param props Chart configuration, behavior flags, layout sizing, and overlay controls.
 * @param ref Forwarded ref to the underlying ECharts React wrapper instance.
 * @returns The chart container and configured ECharts renderer.
 */
const BaseChart = forwardRef<ComponentRef<typeof ReactEChartsCore>, BaseChartProps>(
  (
    {
      option, // ECharts configuration object
      onEvents = {}, // Event handlers for ECharts events (click, mouseover, etc.)
      eChartsRenderer = 'canvas', // Can be 'canvas' or 'svg'
      notMerge = false, // If true, ECharts will recreate and replace the option object on each update with the new one
      autoResize = true, // If true, chart will automatically resize when container size changes
      lazyUpdate = false, // If true, chart will not update immediately on option change it will defer rendering for a later point (improves performance)
      customStyles = {},
      height = '100%',
      width = '100%',
      controlsLeft = null, // optional overlay controls (top-left)
      controlsRight = null, // optional overlay controls (top-right)
      showResetZoom = false,
      emptyMessage = 'No chart data available.',
      onChartReady,
    },
    ref,
  ) => {
    const appChartOptions = useSelector(selectChartOptions);
    const normalizedChartOptions = useMemo(
      () => normalizeChartOptions(appChartOptions),
      [appChartOptions],
    );
    const chartTheme = useMemo(
      () => getChartContainerStyles(normalizedChartOptions),
      [normalizedChartOptions],
    );
    const themedOption = useMemo(
      () => applyChartThemeToOption(option, normalizedChartOptions),
      [option, normalizedChartOptions],
    );
    const containerRef = useRef<HTMLDivElement | null>(null);
    const chartRef = useRef<ComponentRef<typeof ReactEChartsCore> | null>(null);

    // Keep the internal controls and callers connected to the same chart wrapper.
    const setChartRef = useCallback(
      (chart: ComponentRef<typeof ReactEChartsCore> | null) => {
        chartRef.current = chart;
        if (typeof ref === 'function') {
          ref(chart);
        } else if (ref) {
          ref.current = chart;
        }
      },
      [ref],
    );

    const handleResetZoom = () => resetChartZoom(chartRef.current?.getEchartsInstance());

    // Intercept wheel events on the chart container (capture phase) so we can
    // redirect them to the tooltip content when it is visible and scrollable.
    // This runs before ECharts' own handler, giving us a chance to prevent zoom.
    const wheelHandler = useCallback((e: WheelEvent) => {
      // Find the visible tooltip portal created by this chart
      const portal = document.querySelector<HTMLElement>('.echart-tooltip-portal');
      if (!portal || portal.style.display === 'none' || portal.style.visibility === 'hidden') {
        return;
      }

      const content = portal.querySelector<HTMLElement>('.echart-tooltip-content');
      if (!content) return;

      const isScrollable = content.scrollHeight > content.clientHeight;
      if (!isScrollable) return;

      // Tooltip is visible and has overflow - hijack the wheel to scroll it
      e.stopPropagation();
      e.preventDefault();
      content.scrollTop += e.deltaY;
    }, []);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      el.addEventListener('wheel', wheelHandler, { passive: false, capture: true });
      return () => el.removeEventListener('wheel', wheelHandler, { capture: true });
    }, [wheelHandler]);

    return (
      <div
        ref={containerRef}
        className={`fusion-base-chart rounded-3 overflow-hidden ${chartTheme.className || ''}`}
        style={{
          position: 'relative', // Ensures positioned elements inside the chart are positioned within this container
          height,
          width,
          backgroundColor: chartTheme.panelBackground,
          color: chartTheme.textColor,
          ...customStyles,
        }}
      >
        {option && controlsLeft && (
          <div
            style={{
              position: 'absolute',
              top: 2,
              left: 2,
              zIndex: 2,
            }}
          >
            {controlsLeft}
          </div>
        )}

        {option && (controlsRight || showResetZoom) && (
          <div
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              zIndex: 2,
            }}
          >
            {controlsRight}
            {showResetZoom && (
              <Button type="button" size="sm" className="m-1" onClick={handleResetZoom}>
                Reset Zoom
              </Button>
            )}
          </div>
        )}
        {option ? (
          <ReactEChartsCore
            ref={setChartRef}
            option={themedOption}
            onEvents={onEvents}
            opts={{ renderer: eChartsRenderer }} // Sets the renderer type
            echarts={echarts} // Pass in the echarts instance so it knows which components have been registered above for use
            onChartReady={onChartReady}
            notMerge={notMerge}
            autoResize={autoResize}
            lazyUpdate={lazyUpdate}
            style={{ height: '100%', width: '100%' }}
          />
        ) : (
          <p className="m-0 p-3" role="status">
            {emptyMessage}
          </p>
        )}
      </div>
    );
  },
);

BaseChart.displayName = 'BaseChart';

export default BaseChart;
